/**
 * Generador pseudoaleatorio determinista (mulberry32). Se usa en todo el
 * juego en vez de Math.random() para poder reproducir runs y testear
 * matemática del plinko/combate de forma determinista.
 */

export type RngState = number;

/** Crea una función generadora [0,1) a partir de una semilla entera. */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return function rng() {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Envoltorio con utilidades habituales (rango, entero, elección de lista). */
export class Rng {
  private next: () => number;

  constructor(public readonly seed: number) {
    this.next = mulberry32(seed);
  }

  /** Flotante en [0, 1). */
  float(): number {
    return this.next();
  }

  /** Flotante en [min, max). */
  range(min: number, max: number): number {
    return min + this.next() * (max - min);
  }

  /** Entero en [min, max] (ambos inclusive). */
  int(min: number, max: number): number {
    return Math.floor(this.range(min, max + 1));
  }

  /** true con probabilidad `p` (0..1). */
  chance(p: number): boolean {
    return this.next() < p;
  }

  /** Elige un elemento al azar de un arreglo no vacío. */
  pick<T>(items: readonly T[]): T {
    if (items.length === 0) {
      throw new Error('Rng.pick: el arreglo no puede estar vacío');
    }
    const item = items[this.int(0, items.length - 1)];
    return item as T;
  }

  /** Devuelve una copia del arreglo mezclada (Fisher-Yates). */
  shuffle<T>(items: readonly T[]): T[] {
    const copy = [...items];
    for (let i = copy.length - 1; i > 0; i--) {
      const j = this.int(0, i);
      const tmp = copy[i] as T;
      copy[i] = copy[j] as T;
      copy[j] = tmp;
    }
    return copy;
  }
}
